import { Component, input, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { checkIfExpression } from '@jump-tech-frontend/angular-common';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { IfDefinition, QuestionBase } from '@jump-tech-frontend/domain';
import { Subscription } from 'rxjs';
import { QuestionHintComponent } from '../components/question-hint.component';
import { QuestionLabelContentComponent } from '../components/question-label-content.component';

function isPopulated(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value !== null && value !== undefined && value !== '';
}

@Component({
  selector: 'question-wrapper',
  imports: [QuestionLabelContentComponent, QuestionHintComponent],
  template: ` @if (show()) {
    @if (question().label) {
      <label class="form-label" [attr.for]="question().key" [attr.data-qa]="question().key">
        <question-label-content
          [question]="question()"
          [isInvalid]="invalid()"
          [i18ns]="i18ns()"
        ></question-label-content>
      </label>
    }
    <ng-content #component></ng-content>
    <question-hint [question]="question()"></question-hint>
  }`
})
export class QuestionWrapperComponent implements OnInit, OnDestroy {
  question = input.required<QuestionBase<unknown>>();
  form = input.required<FormGroup>();
  showIfContext = input<Record<string, unknown> | undefined>(undefined);
  i18ns = input<I18nKeys>();
  show = signal(false);
  invalid = signal(false);
  private subscription?: Subscription;

  ngOnInit() {
    this.refresh();
    this.subscription = this.form().events.subscribe(() => this.refresh());
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  private refresh(): void {
    const question = this.question();
    const control = this.form().get(question.key);
    const conditionMet = this.conditionMet(control);
    this.toggleControl(control, conditionMet);
    this.show.set(
      conditionMet && question.show !== false && (question.showIfPopulated !== true || isPopulated(control?.value))
    );
    this.invalid.set(!!control?.touched && !!control?.invalid);
  }

  private conditionMet(control: AbstractControl | null): boolean {
    const { data: contextData, ...contextRest } = this.showIfContext() ?? {};
    const target = {
      ...(contextData as Record<string, unknown> | undefined),
      ...contextRest,
      ...(this.form().parent?.getRawValue() ?? {}),
      ...this.form().getRawValue()
    };
    return checkIfExpression(this.question().showIf as IfDefinition, target, control?.value);
  }

  private toggleControl(control: AbstractControl | null, conditionMet: boolean): void {
    if (conditionMet && control?.disabled) {
      control.enable();
    } else if (!conditionMet && control?.enabled) {
      control.disable();
    }
  }
}
